
import React, { useState, useMemo } from 'react';
import { useRememberedState, useRememberedScroll } from '../hooks/useRemembered';
import { ReportHistoryEntry, AppSettings, InspectionItem } from '../types';
import { Calendar, Clock, ChevronLeft, Trash2, FileJson, User, Search, FileText, Copy, ShieldCheck, Layers, Filter, Download, FileArchive, Loader2, RotateCcw } from 'lucide-react';
import { Button } from './Button';
import { generateHistoryListPDF } from '../services/pdfService';
import { computeMissingItems, restoreInspectionItems } from '../services/cloudService';
import { beschreibeNummernWechsel, eintraegeKollisionsfreiEinordnen, EinordnungsErgebnis } from '../services/inspectionItemStatus';
import { istNativeApp } from '../services/nativePlatform';
import { ausgabeFehlerMelden, dateiAusgeben } from '../services/dateiAusgabe';
import { istArchivEintragLoeschbar } from '../services/archivLiveEintrag';

interface Props {
  history: ReportHistoryEntry[];
  onLoad: (report: any) => void;
  onDelete: (id: string) => void;
  onGeneratePdf?: (report: any) => void;
  // When provided, the full report for an entry is loaded on demand (the list
  // itself only carries metadata). Falls back to entry.reportData when absent.
  resolveReport?: (entryId: string) => Promise<any | null>;
  onBack?: () => void;
  embedded?: boolean;
  currentProjectNumber?: string;
  activeSectionId?: string | null;
  settings?: AppSettings; // Added to pass company logo etc. to list export
  // Wiederherstellung verschwundener Mängel aus Snapshots: nur aktiv, wenn
  // projectId UND currentItems übergeben werden.
  projectId?: string;
  /**
   * NUR zum Nachschlagen des Freimelde-QR-Codes fürs Archiv-PDF — rein lesend.
   *
   * Eigene Angabe, weil `projectId` im Lesemodus absichtlich wegfällt (es ist
   * dort der Schalter für das SCHREIBENDE „Mängel wiederherstellen"). Am QR
   * hing es nur zufällig mit: der ZIP-Export übersprang deshalb in der
   * Nur-Ansicht jede Abfrage, und die Archiv-PDFs kamen ohne den Kasten
   * heraus, den die Empfänger damals bekommen haben.
   */
  qrProjectId?: string;
  /**
   * Ist die Online-Freimeldung für das Projekt FREIGESCHALTET
   * (`metadata.freimeldung.aktiv`, Vorgabe an)?
   *
   * DER AUFRUFER RECHNET DAS AUS DEN AKTUELLEN Projekt-Metadaten aus. Die
   * Liste hier hat nur ARCHIV-SCHNAPPSCHÜSSE (`full_report_json`), und in
   * denen steht der Schalterstand des Versandtags eingefroren. Nimmt der
   * Betreiber den Haken zurück, sind alle gedruckten QR-Codes dieses Projekts
   * im selben Moment tot (`report_freimeldung_public` antwortet null) — der
   * Snapshot trüge aber weiter `aktiv: true` und der ZIP-Export legte
   * QR-Codes in jedes Archiv-PDF, die den Empfänger auf „Dieser Link ist
   * nicht mehr gültig" führen. Deshalb NIE `full.metadata` fragen.
   *
   * FEHLT die Angabe, gilt AUS und es wird kein QR nachgeschlagen: wer den
   * aktuellen Stand nicht kennt, darf nicht raten.
   */
  freimeldungAktiv?: boolean;
  currentItems?: InspectionItem[];
  /**
   * Nimmt die wiederhergestellten Einträge in den laufenden Bericht auf — und
   * vergibt dabei ihre laufenden Nummern, weil nur der Aufrufer den Bestand
   * kennt, der in diesem Moment wirklich dasteht (hier liegt nur der Stand vom
   * letzten Render). Er gibt zurück, was er vergeben hat: genau diese Einträge
   * gehen anschliessend in die Cloud, und genau diese Wechsel meldet die
   * Ansicht dem Nutzer.
   *
   * Muss SYNCHRON einordnen und einfügen — ein `await` dazwischen öffnet
   * wieder das Fenster, in dem ein parallel fertig werdender Foto-Upload
   * dieselbe Nummer bekommt.
   */
  onItemsRestored?: (restored: InspectionItem[]) => EinordnungsErgebnis;
  /**
   * Nur-Ansicht: blendet den Loesch-Knopf aus. Sonst wuerde erst
   * "unwiderruflich löschen?" gefragt und danach abgesagt — das liest sich wie
   * ein Fehler, und der Knopf stuende als einziges Werkzeug noch da, waehrend
   * die uebrigen im Lesemodus verschwinden.
   */
  readOnly?: boolean;
}

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

type TimeRange = '1w' | '2w' | '4w' | '3m' | '1y' | 'all';

const TIME_RANGES: { id: TimeRange; label: string }[] = [
    { id: '1w', label: '1 Woche' },
    { id: '2w', label: '2 Wochen' },
    { id: '4w', label: '4 Wochen' },
    { id: '3m', label: '3 Monate' },
    { id: '1y', label: '1 Jahr' },
    { id: 'all', label: 'Gesamt' },
];

export const HistoryView: React.FC<Props> = ({ history, onLoad, onDelete, onGeneratePdf, onBack, embedded = false, currentProjectNumber, activeSectionId, settings, resolveReport, projectId, qrProjectId, freimeldungAktiv = false, currentItems, onItemsRestored, readOnly = false }) => {
  const [searchTerm, setSearchTerm] = useRememberedState(`history.${projectId ?? 'x'}.search`, '');
  const scrollRef = useRememberedScroll(`history.${projectId ?? 'x'}.scroll`);

  // Snapshot fuer das PDF laden und den Durchfuehrenden sicherstellen: neue
  // Archive tragen conductedByName selbst, Altbestand nur in der Zeile
  // (conductor_name). Ohne den Namen wuesste das Protokoll nicht, dass ein
  // Vertreter die Begehung gemacht hat.
  const loadForPdf = async (entry: ReportHistoryEntry) => {
    const full = resolveReport ? await resolveReport(entry.id) : entry.reportData;
    if (!full) return null;
    if (full.metadata?.conductedByName || !entry.conductedByName) return full;
    return { ...full, metadata: { ...full.metadata, conductedByName: entry.conductedByName } };
  };
  const [timeRange, setTimeRange] = useRememberedState<TimeRange>(`history.${projectId ?? 'x'}.range`, 'all');
  const [isZipping, setIsZipping] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const canRestore = !!projectId && !!currentItems;

  const handleRestoreMissing = async (entry: ReportHistoryEntry) => {
      if (!projectId || !currentItems) return;
      setRestoringId(entry.id);
      try {
          const full = resolveReport ? await resolveReport(entry.id) : entry.reportData;
          const snapshotItems: InspectionItem[] = full?.items || [];
          if (!snapshotItems.length) { alert('Dieser Snapshot enthält keine Mängel.'); return; }
          const missing = computeMissingItems(snapshotItems, currentItems);
          if (!missing.length) { alert('Alle Mängel aus diesem Snapshot sind bereits vorhanden — nichts wiederherzustellen.'); return; }
          // Der Snapshot bringt die laufenden Nummern von damals mit. Ist eine
          // davon inzwischen an einen anderen Mangel vergeben, weicht der
          // WIEDERHERGESTELLTE aus — die Einträge im aktuellen Bericht behalten
          // ihre Nummer (sie steht schon in versendeten PDFs und im Plananhang).
          //
          // Die Rückfrage nennt hier bewusst noch KEINE Nummern: welche vergeben
          // werden, entscheidet sich erst gleich, gegen den Bestand von dann.
          // Eine Ankündigung wäre eine Zahl, die am Ende anders lautet.
          if (!window.confirm(
              `${missing.length} fehlende Mängel aus Bericht #${entry.inspectionNumber} wiederherstellen?`
              + '\n\nLaufende Nummern, die im aktuellen Bericht inzwischen vergeben sind, werden dabei neu vergeben.'
              + ' Welche das sind, steht anschliessend in der Meldung.',
          )) return;
          // Einordnen UND in den Bericht aufnehmen in einem Zug — ohne `await`
          // dazwischen. Sonst könnte ein parallel fertig werdender Foto-Upload
          // dieselbe Nummer greifen (er zieht sie aus dem laufenden Bericht,
          // der die Wiederherstellung bis dahin noch nicht enthielte).
          //
          // Erst danach in die Cloud: schlägt der Upsert fehl, stehen die
          // Einträge zwar schon im Bericht, aber das ist die harmlosere Hälfte —
          // der reguläre Projekt-Speicher schreibt sie ohnehin in dieselbe
          // Tabelle. Umgekehrt wäre die Nummer nicht mehr einzufangen.
          const { eintraege: einzuspielen, umnummeriert } = onItemsRestored
              ? onItemsRestored(missing)
              // Ohne Aufnahme-Rückruf (Archiv-Ansicht) bleibt nur der Stand vom
              // letzten Render — immer noch besser als die rohen Snapshot-Nummern.
              : eintraegeKollisionsfreiEinordnen(missing, currentItems);
          await restoreInspectionItems(projectId, einzuspielen);
          const wechselText = beschreibeNummernWechsel(umnummeriert);
          alert(`${missing.length} Mängel wiederhergestellt.` + (wechselText ? `\n\n${wechselText}` : ''));
      } catch (e: any) {
          console.error('Mängel-Wiederherstellung fehlgeschlagen:', e);
          alert('Wiederherstellung fehlgeschlagen: ' + (e?.message || e));
      } finally {
          setRestoringId(null);
      }
  };

  // Calculate start date based on selected range
  const startDate = useMemo(() => {
      if (timeRange === 'all') return null;
      
      const date = new Date();
      date.setHours(0, 0, 0, 0); // Start of today

      switch (timeRange) {
          case '1w': date.setDate(date.getDate() - 7); break;
          case '2w': date.setDate(date.getDate() - 14); break;
          case '4w': date.setDate(date.getDate() - 28); break;
          case '3m': date.setMonth(date.getMonth() - 3); break;
          case '1y': date.setFullYear(date.getFullYear() - 1); break;
      }
      return date;
  }, [timeRange]);

  const filteredHistory = useMemo(() => {
      return history.filter(entry => {
        // 1. Filter by Project (if embedded)
        const matchesProject = currentProjectNumber 
          ? (entry.projectNumber === currentProjectNumber || entry.reportData.metadata.projectNumber === currentProjectNumber)
          : true;
        
        // 2. Filter by Search
        const matchesSearch = searchTerm 
            ? (entry.projectName.toLowerCase().includes(searchTerm.toLowerCase()) || (entry.projectNumber || '').toLowerCase().includes(searchTerm.toLowerCase()))
            : true;
        
        // 3. Filter by Section (STRICT FILTERING)
        // If activeSectionId is set (and not 'all'), we only show entries that match this section ID.
        // If activeSectionId is 'all', we show everything.
        let matchesSection = true;
        if (activeSectionId && activeSectionId !== 'all') {
             // Check the sectionId stored in the history entry (mapped from DB column)
             // Fallback to metadata inside JSON if column data missing
             const entrySectionId = (entry as any).sectionId || entry.reportData.metadata.sectionId;
             matchesSection = entrySectionId === activeSectionId;
        }

        // 4. Filter by Time Range
        let matchesDate = true;
        if (startDate) {
            const entryDate = new Date(entry.date);
            matchesDate = entryDate >= startDate;
        }
        
        return matchesProject && matchesSearch && matchesSection && matchesDate;
      });
  }, [history, currentProjectNumber, searchTerm, activeSectionId, startDate]);

  const groupedHistory = useMemo(() => {
      const groups: Record<string, ReportHistoryEntry[]> = {};
      filteredHistory.forEach(entry => {
          const key = entry.projectName || 'Unbenanntes Projekt';
          if (!groups[key]) groups[key] = [];
          groups[key].push(entry);
      });
      
      // Sort within groups by date descending
      Object.keys(groups).forEach(key => {
          groups[key].sort((a, b) => {
              const dateA = new Date(a.date).getTime();
              const dateB = new Date(b.date).getTime();
              if (dateA !== dateB) return dateB - dateA;
              return b.inspectionNumber - a.inspectionNumber;
          });
      });
      
      return groups;
  }, [filteredHistory]);

  // Helper to get section Name from metadata
  const getSectionName = (entry: ReportHistoryEntry) => {
      const meta = entry.reportData.metadata;
      if (meta.sectionId && meta.globalData?.sections) {
          const sec = meta.globalData.sections.find((s: any) => s.id === meta.sectionId);
          return sec ? sec.name : 'Unbekannter Abschnitt';
      }
      return null;
  };

  const handleExportList = () => {
      const rangeLabel = TIME_RANGES.find(r => r.id === timeRange)?.label || 'Benutzerdefiniert';
      const doc = generateHistoryListPDF(filteredHistory, rangeLabel, settings);
      // Android-App: Blob-Downloads kommen in der WebView nicht an → Teilen-Dialog.
      if (istNativeApp()) {
          void dateiAusgeben(doc.output('blob'), `Begehungshistorie_${timeRange}.pdf`).catch(ausgabeFehlerMelden);
      } else {
          doc.save(`Begehungshistorie_${timeRange}.pdf`);
      }
  };

  const handleDownloadAllZip = async () => {
    if (filteredHistory.length === 0) return;
    
    setIsZipping(true);
    try {
      const JSZip = (await import('jszip')).default;
      const { saveAs } = await import('file-saver');
      const { generatePDF } = await import('../services/pdfService');
      const { ladeFreimeldungQrFuerBericht } = await import('../services/freimeldungService');

      const zip = new JSZip();
      
      // Use a folder in the zip
      const folder = zip.folder("Berichte");
      
      for (const entry of filteredHistory) {
        // Generate PDF for each entry (load full report on demand when lazy)
        const full = await loadForPdf(entry);
        if (!full) continue;
        // Rein lesend: das Archiv-PDF traegt denselben QR-Code wie die damals
        // versendete Mail. Ohne Berichtsnummer oder ohne Link bleibt es null,
        // und der Kasten faellt weg — hier entsteht NIE ein neuer Link.
        //
        // Ueber `qrProjectId`, NICHT ueber `projectId`: letzteres faellt im
        // Lesemodus weg, weil daran das schreibende Wiederherstellen haengt.
        // Ein Nachschlagen ist keine Aenderung und darf davon nicht abhaengen.
        //
        // Ist die Online-Freimeldung fuer das Projekt nicht freigeschaltet
        // (`metadata.freimeldung.aktiv`, Vorgabe an), wird gar nicht gefragt:
        // die gedruckten Links sind dann gesperrt, und ein QR-Code, der auf
        // „nicht mehr gueltig" fuehrt, gehoert in kein Archiv-PDF. Der Dienst
        // prueft das nicht selbst — er bekommt nur Kennungen.
        //
        // GEFRAGT WIRD DAS PROP, NIE `full.metadata`: `full` ist der
        // SCHNAPPSCHUSS des damals versendeten Berichts und traegt den
        // Schalterstand vom Versandtag. Nach dem Abschalten stuende dort
        // weiter `aktiv: true`, und der Export legte genau die QR-Codes ins
        // Archiv-PDF, die die Datenbank nicht mehr beantwortet. Der aktuelle
        // Stand kommt vom Aufrufer, der das Projekt geladen hat.
        const qrProjekt = qrProjectId || projectId;
        const nummer = entry.inspectionNumber ?? full.metadata.inspectionNumber;
        const freimeldungQr = qrProjekt && nummer && freimeldungAktiv
          ? await ladeFreimeldungQrFuerBericht(qrProjekt, nummer, (entry as any).sectionId ?? full.metadata.sectionId ?? null, window.location.origin, window.location.pathname)
          : null;
        const doc = await generatePDF(full, settings || full.settings, history, undefined, { freimeldungQr });
        const pdfBlob = doc.output('blob');
        
        // Create a clean filename
        const dateStr = entry.date.split('T')[0];
        const filename = `${dateStr}_Bericht_Nr_${entry.inspectionNumber}.pdf`;
        
        if (folder) {
            folder.file(filename, pdfBlob);
        }
      }
      
      const content = await zip.generateAsync({ type: 'blob' });
      const zipFilename = `Berichtsarchiv_${currentProjectNumber || entryProjectName()}_${new Date().toISOString().split('T')[0]}.zip`;
      if (istNativeApp()) {
        // Android-App: Teilen-Dialog statt Download. Abgewartet, damit der
        // Knopf gesperrt bleibt, bis die (große) Datei im Cache liegt und der
        // Dialog zu ist — ein zweites Tippen liefe sonst ins „Teilen läuft schon".
        await dateiAusgeben(content, zipFilename).catch(ausgabeFehlerMelden);
      } else {
        saveAs(content, zipFilename);
      }
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert('Fehler beim Erstellen der ZIP-Datei.');
    } finally {
      setIsZipping(false);
    }
  };

  const entryProjectName = () => {
      if (filteredHistory.length > 0) return filteredHistory[0].projectName;
      return 'Projekt';
  };

  return (
    <div className={`flex flex-col h-full ${embedded ? 'bg-transparent' : 'bg-slate-50'}`}>
      {!embedded && (
        <header className="bg-slate-900 text-white p-6 shadow-lg flex justify-between items-center shrink-0">
            <div className="flex items-center gap-4">
                {onBack && (
                  <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                      <ChevronLeft className="w-6 h-6" />
                  </button>
                )}
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Calendar className="text-blue-400" /> Begehungshistorie
                </h2>
            </div>
            <div className="flex flex-col items-end">
                 <span className="text-xs font-mono text-slate-400">{filteredHistory.length} Berichte angezeigt</span>
                 {timeRange !== 'all' && <span className="text-[11px] text-blue-400">Filter aktiv</span>}
            </div>
        </header>
      )}

      {embedded && (
          <div className="p-6 pb-0">
              <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-bold text-blue-800 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600"/>
                      Berichts-Archiv
                  </h2>
                  <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-500 font-medium">
                      {filteredHistory.length} Einträge
                  </span>
              </div>
              <div className="flex items-start gap-2 bg-blue-50 p-3 rounded-lg border border-blue-100 text-xs text-blue-800">
                  <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-blue-600"/>
                  <p>Archivierte Berichte sind schreibgeschützt und in der Datenbank gesichert. Sie können diese hier als PDF einsehen.</p>
              </div>
          </div>
      )}

      <main className={`flex-1 flex flex-col min-h-0 ${embedded ? 'p-6 max-w-6xl mx-auto w-full' : 'p-4 md:p-8 max-w-5xl mx-auto w-full'}`}>
        <div className="mb-6 flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input 
                    type="text" 
                    placeholder="Archiv durchsuchen (Nr., Datum...)" 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none shadow-sm text-sm"
                />
            </div>
            
            {/* TIME RANGE FILTER */}
            <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
                <div className="flex items-center gap-1 px-2 border-r border-slate-100">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-bold text-slate-500 uppercase">Zeitraum</span>
                </div>
                <select 
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                    className="bg-transparent text-sm font-medium text-slate-700 outline-none cursor-pointer py-1 px-1 hover:text-blue-600"
                >
                    {TIME_RANGES.map(range => (
                        <option key={range.id} value={range.id}>{range.label}</option>
                    ))}
                </select>
            </div>

            {/* LIST EXPORT BUTTON */}
            <Button 
                variant="secondary" 
                onClick={handleExportList}
                className="whitespace-nowrap text-xs h-[38px] bg-white border border-slate-200 hover:border-blue-300 hover:text-blue-600 shadow-sm"
                title="Aktuelle Auswahl als Liste drucken"
                icon={<Download className="w-4 h-4" />}
            >
                Liste exportieren
            </Button>

            {/* ZIP DOWNLOAD BUTTON */}
            {embedded && filteredHistory.length > 0 && (
                <Button 
                    variant="primary" 
                    onClick={handleDownloadAllZip}
                    disabled={isZipping}
                    className="whitespace-nowrap text-xs h-[38px] bg-blue-600 hover:bg-blue-700 shadow-md flex items-center gap-2"
                    title="Alle Berichte in der Auswahl als ZIP herunterladen"
                    icon={isZipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />}
                >
                    {isZipping ? 'Erstelle ZIP...' : 'Alle als ZIP'}
                </Button>
            )}

            {!embedded && currentProjectNumber && (
                <div className="bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-xs font-bold border border-blue-100 whitespace-nowrap">
                    Projekt: {currentProjectNumber}
                </div>
            )}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
          {filteredHistory.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-slate-200">
              <FileJson className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <p className="text-slate-500 font-medium">Keine Berichte im gewählten Zeitraum gefunden.</p>
              {timeRange !== 'all' && (
                  <button onClick={() => setTimeRange('all')} className="text-blue-600 text-sm mt-2 hover:underline">Filter zurücksetzen</button>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {Object.keys(groupedHistory).sort().map((groupName) => (
                <div key={groupName}>
                    {(Object.keys(groupedHistory).length > 1 || embedded) && (
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-200 sticky top-0 bg-slate-50/95 backdrop-blur z-10 pt-2">
                             <h3 className="font-bold text-lg text-slate-800">{groupName}</h3>
                             <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">{groupedHistory[groupName].length}</span>
                        </div>
                    )}
                    <div className="space-y-4">
                        {groupedHistory[groupName].map((entry) => {
                          const sectionName = getSectionName(entry);
                          
                          return (
                            <div 
                              key={entry.id} 
                              className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-blue-300 transition-all group"
                            >
                              <div className="flex items-start gap-4 flex-1">
                                 <div className="w-12 h-12 bg-slate-100 text-slate-600 rounded-xl flex items-center justify-center font-bold text-lg shadow-inner shrink-0 border border-slate-200">
                                     #{entry.inspectionNumber}
                                 </div>
                                 <div className="flex-1 min-w-0">
                                     <div className="flex items-center gap-2">
                                         <h3 className="font-bold text-slate-800 text-lg group-hover:text-blue-600 transition-colors truncate">{entry.projectName}</h3>
                                         {sectionName && (
                                             <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100 flex items-center gap-1">
                                                 <Layers className="w-3 h-3"/> {sectionName}
                                             </span>
                                         )}
                                     </div>
                                     <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                                         <span className="flex items-center">
                                           <Calendar className="w-4 h-4 mr-1 text-slate-400"/> 
                                           {formatDate(entry.date)}
                                         </span>
                                         <span className="flex items-center"><Clock className="w-4 h-4 mr-1 text-slate-400"/> {entry.time} Uhr</span>
                                         <span className="flex items-center px-2 py-0.5 bg-slate-100 rounded text-xs font-bold text-slate-600">
                                             <User className="w-3 h-3 mr-1"/> {entry.conductedByName}
                                         </span>
                                     </div>
                                 </div>
                              </div>

                              <div className="flex items-center gap-2 border-t md:border-t-0 pt-3 md:pt-0">
                                 {/* ANPASSUNG: WENN EMBEDDED, NUR PDF BUTTON ANZEIGEN */}
                                 {embedded ? (
                                     <Button 
                                        variant="primary" 
                                        onClick={async () => { if (!onGeneratePdf) return; const full = await loadForPdf(entry); if (full) onGeneratePdf(full); }}
                                        className="py-2 px-4 text-xs bg-blue-600 hover:bg-blue-700 shadow-sm"
                                        icon={<FileText className="w-4 h-4"/>}
                                     >
                                         Bericht öffnen (PDF)
                                     </Button>
                                 ) : (
                                     <>
                                         {onGeneratePdf && (
                                             <Button variant="secondary" onClick={async () => { const full = await loadForPdf(entry); if (full) onGeneratePdf(full); }} className="py-2 px-3 text-xs" icon={<FileText className="w-4 h-4"/>}>
                                                 PDF
                                             </Button>
                                         )}
                                         <Button 
                                            variant="primary" 
                                            onClick={() => onLoad(entry.reportData)} 
                                            className="py-2 px-3 text-xs bg-blue-600 hover:bg-blue-700" 
                                            icon={<Copy className="w-3 h-3"/>}
                                         >
                                            Kopie bearbeiten
                                         </Button>
                                     </>
                                 )}

                                 {canRestore && (
                                     <Button
                                        variant="secondary"
                                        onClick={() => handleRestoreMissing(entry)}
                                        disabled={restoringId !== null}
                                        className="py-2 px-3 text-xs"
                                        icon={restoringId === entry.id ? <Loader2 className="w-4 h-4 animate-spin"/> : <RotateCcw className="w-4 h-4"/>}
                                        title="Fehlende Mängel aus diesem Snapshot wiederherstellen"
                                     >
                                         Mängel wiederherstellen
                                     </Button>
                                 )}
                                 {/* Der "aktuelle Stand" (Buchhaltung) ist keine Archivzeile und hat nichts zu loeschen. */}
                                 {!readOnly && istArchivEintragLoeschbar(entry) && (
                                     <button
                                        onClick={() => { if(window.confirm('Eintrag unwiderruflich aus Archiv löschen?')) onDelete(entry.id); }}
                                        className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                        title="Löschen"
                                     >
                                         <Trash2 className="w-5 h-5" />
                                     </button>
                                 )}
                              </div>
                            </div>
                          );
                      })}
                    </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
